import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import JsBarcode from 'jsbarcode';
import { Loader2, Printer, RefreshCw, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { useRegenerateManagerCode } from './staff.api';

/**
 * Somebody's manager code, as a scannable barcode.
 *
 * The point of the page is the print-out: a manager authorises a till by
 * having their code scanned, so the code has to leave the screen as something
 * a scanner can read. It is drawn as EAN-13, which is what the original used.
 *
 * A code that is not valid EAN-13 falls back to showing the digits large
 * rather than an empty box — better a code that can be typed than one that
 * looks broken.
 */
export function ManagerCodeDialog({
  name,
  userId,
  code,
  canRegenerate,
  open,
  onOpenChange,
  onRegenerated,
}: {
  name: string;
  userId: string;
  code: string;
  canRegenerate: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegenerated: (code: string) => void;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);
  const regenerate = useRegenerateManagerCode();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!open || !canvas || !code) return;
    try {
      JsBarcode(canvas, code, {
        format: 'EAN13',
        width: 2,
        height: 80,
        displayValue: false,
        margin: 8,
      });
      setDrawn(true);
    } catch {
      setDrawn(false);
    }
  }, [open, code]);

  /**
   * Print by writing a whole document into the new window and waiting for it
   * to load before calling print. Copying `innerHTML` across, as the original
   * did, prints an unstyled page — and often a blank one, because the image
   * has not decoded by the time the dialog opens.
   */
  function print() {
    const canvas = canvasRef.current;
    const image = drawn && canvas ? canvas.toDataURL('image/png') : null;
    const sheet = window.open('', '_blank', 'width=420,height=560');
    if (!sheet) {
      toast.error(t('staff.printBlocked'));
      return;
    }

    sheet.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(name)}</title>
<style>
  body { font-family: system-ui, sans-serif; text-align: center; padding: 24px; }
  h1 { font-size: 16px; font-weight: 700; margin: 0 0 12px; }
  code { font-size: 20px; letter-spacing: 2px; }
  img { max-width: 100%; }
</style></head>
<body>
  <h1>${escapeHtml(name)}</h1>
  ${image ? `<img src="${image}" alt="">` : ''}
  <div><code>${escapeHtml(code)}</code></div>
  <script>
    window.onload = function () { window.focus(); window.print(); window.close(); };
  ${'<'}/script>
</body></html>`);
    sheet.document.close();
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'bg-background fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border p-4 shadow-xl',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <DialogPrimitive.Title className="pr-8 text-sm font-semibold">
            {t('staff.managerCode')}
          </DialogPrimitive.Title>
          <DialogPrimitive.Close className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-3 right-3 rounded p-1 focus-visible:ring-2 focus-visible:outline-none">
            <X className="size-4" />
            <span className="sr-only">{t('common.cancel')}</span>
          </DialogPrimitive.Close>

          <div className="mt-4 space-y-4">
          <div>
            <p className="text-sm font-medium">{name}</p>
            <div className="flex items-center gap-1">
              <code className="font-mono text-lg tracking-widest">{code}</code>
              <CopyButton value={code} />
            </div>
          </div>

          <div className="flex justify-center rounded-xl border bg-white p-3">
            <canvas ref={canvasRef} className={drawn ? '' : 'hidden'} />
            {!drawn && (
              <p className="text-muted-foreground py-6 text-center text-sm">
                {t('staff.barcodeUnavailable')}
              </p>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            {canRegenerate && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={regenerate.isPending}>
                    {regenerate.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <RefreshCw className="size-4" />
                    )}
                    {t('staff.generateNewCode')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('staff.generateNewCode')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('staff.generateNewCodePrompt', { name })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() =>
                        regenerate.mutate(userId, {
                          onSuccess: (next) => {
                            onRegenerated(next);
                            toast.success(t('staff.newCodeCreated'));
                          },
                          onError: () => toast.error(t('staff.actionFailed')),
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button onClick={print}>
              <Printer className="size-4" />
              {t('staff.print')}
            </Button>
          </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function escapeHtml(value: string) {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char] ?? char
  );
}
